import React from 'react';
import { Button } from '@/components/ui/button';
import { BasicFSIResponse } from '@/services/fsi';
import { X, Calculator, AlertTriangle, Home, Ruler, DollarSign, Building, FileText, Loader2, Clock, XCircle, Info } from 'lucide-react';

// Loading component for FSI Analysis
const FSILoadingDisplay: React.FC = () => {
  const loadingMessages = [
    {
      icon: <Calculator className="h-6 w-6 text-blue-600" />,
      title: "Calculating Basic FSI",
      subtitle: "Processing floor space index parameters"
    },
    {
      icon: <Building className="h-6 w-6 text-green-600" />,
      title: "Analyzing Plot Data",
      subtitle: "Evaluating development potential"
    },
    {
      icon: <DollarSign className="h-6 w-6 text-purple-600" />,
      title: "Computing Estimates",
      subtitle: "Calculating financial projections"
    }
  ];

  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, []);

  const currentMessage = loadingMessages[currentMessageIndex];

  return (
    <div className="flex flex-col items-center justify-center py-12 px-8">
      <div className="relative mb-6">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
        </div>
      </div>
      
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          {currentMessage.icon}
          <h3 className="text-lg font-bold text-gray-800">{currentMessage.title}</h3>
        </div>
        <p className="text-gray-600 max-w-sm">{currentMessage.subtitle}</p>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <Clock className="h-4 w-4" />
        <span>Processing your request...</span>
      </div>
      
      <div className="mt-4 flex space-x-2">
        {loadingMessages.map((_, index) => (
          <div
            key={index}
            className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              index === currentMessageIndex ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

interface FSIAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  fsiData: BasicFSIResponse | null;
  isLoading: boolean;
  error: string | null;
}

const FSIAnalysisModal: React.FC<FSIAnalysisModalProps> = ({
  isOpen,
  onClose,
  fsiData,
  isLoading,
  error
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
      <div 
        className="w-full bg-white rounded-t-2xl shadow-2xl transform transition-transform duration-300 ease-out animate-in slide-in-from-bottom-full"
        style={{ maxHeight: '90vh' }}
      >
        {/* Modal Header */}
        <div className="bg-[#179246] text-white px-6 py-4 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calculator className="h-6 w-6" />
              <div>
                <h2 className="text-xl font-bold">FSI Analysis Report</h2>
                <p className="text-emerald-100 text-sm">
                  {fsiData?.fp_information?.name || 'Plot Analysis'}
                </p>
              </div>
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {isLoading ? (
            <FSILoadingDisplay />
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 px-8">
              <div className="mb-6">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-800 mb-2">Analysis Failed</h3>
                <p className="text-gray-600 text-center max-w-md">{error}</p>
              </div>
              <Button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Close
              </Button>
            </div>
          ) : fsiData ? (
            <div className="space-y-6">
              {/* Plot Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Home className="h-5 w-5 text-[#179246]" />
                  <h3 className="font-semibold text-gray-800">Plot Information</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600 mb-1">Plot ID (GID)</p>
                    <span className="font-medium">{fsiData.fp_information?.gid}</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Area</p>
                    <span className="font-medium">{fsiData.fp_information?.area_sqm?.toLocaleString()} sq.m</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Zone</p>
                    <span className="font-medium">{fsiData.zone_analysis?.zone_code}</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Plot Name</p>
                    <span className="font-medium">{fsiData.fp_information?.name}</span>
                  </div>
                </div>
              </div>

              {/* FSI Calculations */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ruler className="h-5 w-5 text-[#179246]" />
                  <h3 className="font-semibold text-gray-800">FSI Calculations</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <p className="text-gray-600 mb-1">Base FSI</p>
                    <span className="font-bold text-lg text-blue-600">{fsiData.fsi_calculations?.base_fsi}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 mb-1">Applied FSI</p>
                    <span className="font-bold text-lg text-green-600">{fsiData.fsi_calculations?.applied_fsi}</span>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-600 mb-1">Max Possible FSI</p>
                    <span className="font-bold text-lg text-purple-600">{fsiData.fsi_calculations?.maximum_possible_fsi}</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Buildable Area</p>
                    <span className="font-medium">{fsiData.fsi_calculations?.gross_buildable_area_sqm?.toLocaleString()} sq.m</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Ground Floor Area</p>
                    <span className="font-medium">{fsiData.fsi_calculations?.max_ground_floor_area_sqm?.toLocaleString()} sq.m</span>
                  </div>
                  <div>
                    <p className="text-gray-600 mb-1">Premium FSI</p>
                    <span className="font-medium">{fsiData.fsi_calculations?.premium_fsi}</span>
                  </div>
                </div>
              </div>

              {/* Financial Analysis */}
              {fsiData.financial_estimates && (
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="h-5 w-5 text-[#179246]" />
                    <h3 className="font-semibold text-gray-800">Financial Estimates</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">Construction Cost</p>
                      <span className="font-bold text-lg text-red-600">
                        ₹{fsiData.financial_estimates.construction?.total_cost
                          ? (fsiData.financial_estimates.construction.total_cost / 10000000).toFixed(1)
                          : '0'}Cr
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">Revenue Potential</p>
                      <span className="font-bold text-lg text-green-600">
                        ₹{fsiData.financial_estimates.revenue?.total_revenue
                          ? (fsiData.financial_estimates.revenue.total_revenue / 10000000).toFixed(1)
                          : '0'}Cr
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">Profit Margin</p>
                      <span className="font-bold text-lg text-blue-600">
                        {fsiData.financial_estimates.profitability?.margin_percentage?.toFixed(1)}%
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-600 mb-1">ROI</p>
                      <span className="font-bold text-lg text-purple-600">
                        {fsiData.financial_estimates.profitability?.roi_percentage?.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Road Analysis */}
              {fsiData.road_analysis && (
                <div className="bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="h-5 w-5 text-[#179246]" />
                    <h3 className="font-semibold text-gray-800">Road Analysis</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Road Width</p>
                      <span className="font-medium">{fsiData.road_analysis.road_width_m}m ({fsiData.road_analysis.road_width_ft}ft)</span>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Premium Percentage</p>
                      <span className="font-medium">{fsiData.road_analysis.premium_percentage}%</span>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Category</p>
                      <span className="font-medium">{fsiData.road_analysis.road_category}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Height Compliance */}
              {fsiData.height_compliance && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building className="h-5 w-5 text-[#179246]" />
                    <h3 className="font-semibold text-gray-800">Height Compliance</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600 mb-1">Building Height</p>
                      <span className="font-medium">{fsiData.height_compliance.building_height_m}m</span>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Max Allowed</p>
                      <span className="font-medium">{fsiData.height_compliance.max_height_allowed_m}m</span>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Compliant</p>
                      <span className={`font-medium ${fsiData.height_compliance.height_compliant ? 'text-green-600' : 'text-red-600'}`}>
                        {fsiData.height_compliance.height_compliant ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-600 mb-1">Risk Level</p>
                      <span className="font-medium">{fsiData.height_compliance.risk_level}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Warnings and Recommendations */}
              {((fsiData.flags_and_warnings && fsiData.flags_and_warnings.length > 0) || 
                (fsiData.recommendations && fsiData.recommendations.length > 0)) && (
                <div className="space-y-4">
                  {fsiData.flags_and_warnings && fsiData.flags_and_warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-semibold text-yellow-800 mb-2">Flags & Warnings</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
                        {fsiData.flags_and_warnings.map((flag, index) => (
                          <li key={index}>{flag.message} - {flag.action}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {fsiData.recommendations && fsiData.recommendations.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-800 mb-2">Recommendations</h4>
                      <ul className="list-disc list-inside space-y-1 text-sm text-blue-700">
                        {fsiData.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Analysis Metadata */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
                <div className="flex items-center gap-2 mb-2">
                  <Calculator className="h-4 w-4" />
                  <span className="font-medium">Analysis Information</span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="font-medium">Generated:</span>
                    <div>
                      {fsiData.calculation_metadata?.calculated_at
                        ? new Date(fsiData.calculation_metadata.calculated_at).toLocaleString()
                        : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">SLA Version:</span>
                    <span>{fsiData.calculation_metadata?.sla_version}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-8">
              <div className="mb-6">
                <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-600 mb-2">No Data Available</h3>
                <p className="text-gray-500 text-center max-w-md">
                  No FSI analysis data is available to display.
                </p>
              </div>
              <Button
                onClick={onClose}
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        {fsiData && !isLoading && !error && (
          <div className="bg-gray-50 px-6 py-4 flex gap-3 rounded-b-2xl">
            <Button 
              onClick={onClose}
              variant="outline" 
              className="flex-1"
            >
              Close
            </Button>
            <Button 
              onClick={() => {
                alert('PDF export feature coming soon!');
              }}
              className="flex-1 bg-[#179246] hover:bg-[#147a3c]"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export PDF
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FSIAnalysisModal;